import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './JourJ.css';

// ─── Helpers ──────────────────────────────────

function fmt$(v) {
  if (v == null) return '—';
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

function parseQRValue(raw) {
  // ESCAMEL-CMD-ESC-2026-047
  const match = raw.match(/ESCAMEL-CMD-(.+)/);
  return match ? match[1] : raw;
}

// ─── Onglets ───────────────────────────────────

const ONGLETS = [
  { key: 'stats',      label: 'Stats',      icon: 'analytics' },
  { key: 'scanner',   label: 'Scanner',    icon: 'photo'     },
  { key: 'liste',     label: 'Liste',      icon: 'liste'     },
  { key: 'production',label: 'Production', icon: 'viande'    },
];

// ─── Stats live ────────────────────────────────

function VueStats({ stats, evenement }) {
  return (
    <div className="jj-stats">
      <div className="jj-ev-nom">{evenement?.nom}</div>
      <div className="jj-kpi-grille">
        <div className="jj-kpi jj-kpi-or">
          <div className="jj-kpi-val">{stats.nb_remises}</div>
          <div className="jj-kpi-lbl">Remises</div>
        </div>
        <div className="jj-kpi jj-kpi-gold">
          <div className="jj-kpi-val">{stats.nb_pretes}</div>
          <div className="jj-kpi-lbl">Prêtes</div>
        </div>
        <div className="jj-kpi jj-kpi-muted">
          <div className="jj-kpi-val">{stats.nb_attente}</div>
          <div className="jj-kpi-lbl">En attente</div>
        </div>
      </div>
      <div className="jj-revenus-bloc">
        <div className="jj-revenus-val">{fmt$(stats.revenus)}</div>
        <div className="jj-revenus-lbl muted">Revenus encaissés</div>
      </div>
      <div className="jj-progress-wrap">
        <div className="jj-progress-label muted">
          {stats.nb_remises}/{stats.nb_confirmees} commandes remises
        </div>
        <div className="jj-progress-barre">
          <div
            className="jj-progress-fill"
            style={{ width: stats.nb_confirmees > 0
              ? `${Math.min(100, (stats.nb_remises / stats.nb_confirmees) * 100)}%`
              : '0%',
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Résultat scan ─────────────────────────────

function ResultatScan({ commande, onMarquerRemise, chargementAction }) {
  if (!commande) return null;
  const { statut, slot_pickup, numero_conf, client_prenom, client_nom, items } = commande;
  const itemsParsed = typeof items === 'string' ? JSON.parse(items) : (items || []);

  let headerIcon, headerTexte, headerClasse;
  if (statut === 'remise') {
    headerIcon  = 'fermer';
    headerTexte = 'Déjà remise';
    headerClasse = 'scan-resultat-header scan-header-remise';
  } else if (statut === 'prete' || statut === 'confirmee') {
    headerIcon  = 'succes';
    headerTexte = 'COMMANDE TROUVÉE';
    headerClasse = 'scan-resultat-header scan-header-ok';
  } else {
    headerIcon  = 'alerte';
    headerTexte = `Statut : ${statut}`;
    headerClasse = 'scan-resultat-header scan-header-alerte';
  }

  return (
    <div className={`scan-resultat ${statut === 'remise' ? 'scan-deja-remise' : 'scan-ok'}`}>
      <div className={headerClasse}>
        <Icon name={headerIcon} size={18} />
        {headerTexte}
      </div>
      <div className="scan-num">{numero_conf}</div>
      <div className="scan-client">{client_prenom} {client_nom}</div>
      <div className="scan-slot muted">Créneau : {slot_pickup}</div>
      <div className="scan-items">
        {itemsParsed.map((item, i) => (
          <div key={i} className="scan-item">
            {item.quantite}&times; {item.label || item.taille}
          </div>
        ))}
      </div>
      {statut === 'remise' ? (
        <div className="scan-deja-msg muted">
          Cette commande a déjà été remise.
        </div>
      ) : (
        <button
          className="btn btn-primary btn-full btn-marquer-remise"
          onClick={onMarquerRemise}
          disabled={chargementAction}
        >
          {chargementAction ? 'Mise à jour…' : 'Marquer comme remise'}
        </button>
      )}
    </div>
  );
}

// ─── Scanner QR ────────────────────────────────

function VueScanner({ evenementId }) {
  const [scannerActif, setScannerActif]       = useState(false);
  const [numeroManuel, setNumeroManuel]       = useState('');
  const [commande, setCommande]               = useState(null);
  const [erreurScan, setErreurScan]           = useState('');
  const [chargementRecherche, setChgRech]     = useState(false);
  const [chargementAction, setChgAction]      = useState(false);
  const [msgSucces, setMsgSucces]             = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  const chercher = useCallback(async (numero) => {
    if (!numero) return;
    setChgRech(true);
    setErreurScan('');
    setCommande(null);
    try {
      const data = await api.get(`/evenements/${evenementId}/commande-par-numero/${numero}`);
      setCommande(data);
    } catch (err) {
      setErreurScan(err.response?.data?.error || `Commande "${numero}" introuvable.`);
    } finally {
      setChgRech(false);
    }
  }, [evenementId]);

  async function marquerRemise() {
    if (!commande) return;
    setChgAction(true);
    try {
      await api.put(`/precommandes/${commande.id}/statut`, { statut: 'remise' });
      setCommande(prev => ({ ...prev, statut: 'remise' }));
      setMsgSucces(`Commande ${commande.numero_conf} remise`);
      setTimeout(() => {
        setMsgSucces('');
        setCommande(null);
        setScannerActif(false);
      }, 2500);
    } catch {
      setErreurScan('Erreur lors de la mise à jour.');
    } finally {
      setChgAction(false);
    }
  }

  // Démarrer scanner HTML5
  useEffect(() => {
    if (!scannerActif) {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
        html5QrCodeRef.current = null;
      }
      return;
    }
    // Chargement dynamique de html5-qrcode
    import('html5-qrcode').then(({ Html5Qrcode }) => {
      const scanner = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = scanner;
      scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          const numero = parseQRValue(decodedText);
          scanner.stop().catch(() => {});
          setScannerActif(false);
          chercher(numero);
        },
        () => {} // erreurs silencieuses
      ).catch(err => {
        setErreurScan('Impossible d\'accéder à la caméra : ' + err.message);
        setScannerActif(false);
      });
    });

    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, [scannerActif, chercher]);

  return (
    <div className="jj-scanner">
      {msgSucces && (
        <div className="jj-succes-msg">
          <Icon name="succes" size={16} />
          {msgSucces}
        </div>
      )}

      {/* Bouton caméra */}
      {!scannerActif && !commande && (
        <button
          className="btn btn-primary btn-full btn-camera"
          onClick={() => { setScannerActif(true); setErreurScan(''); setCommande(null); }}
        >
          <Icon name="photo" size={20} />
          Scanner une commande
        </button>
      )}

      {scannerActif && (
        <div className="scanner-wrap">
          <div id="qr-reader" ref={scannerRef} className="qr-reader" />
          <button
            className="btn btn-ghost btn-full"
            onClick={() => setScannerActif(false)}
          >
            Annuler
          </button>
        </div>
      )}

      {/* Recherche manuelle */}
      {!scannerActif && (
        <div className="scanner-manuel">
          <div className="scanner-manuel-titre muted">Ou saisir le numéro manuellement :</div>
          <div className="scanner-manuel-row">
            <input
              className="input-manual"
              placeholder="Ex: ESC-2026-047"
              value={numeroManuel}
              onChange={e => setNumeroManuel(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && chercher(numeroManuel)}
            />
            <button
              className="btn btn-primary"
              onClick={() => chercher(numeroManuel)}
              disabled={!numeroManuel || chargementRecherche}
            >
              {chargementRecherche ? '…' : 'Chercher'}
            </button>
          </div>
        </div>
      )}

      {erreurScan && (
        <div className="jj-erreur">
          <Icon name="alerte" size={14} />
          {erreurScan}
        </div>
      )}

      <ResultatScan
        commande={commande}
        onMarquerRemise={marquerRemise}
        chargementAction={chargementAction}
      />
    </div>
  );
}

// ─── Vue liste par slot ────────────────────────

function VueListe({ commandes, slots, onMarquerStatut, chargementAction }) {
  const [slotOuvert, setSlotOuvert] = useState(slots[0]?.heure || null);
  const [cmdSelectionnee, setCmdSel] = useState(null);

  const parSlot = {};
  for (const s of slots) {
    parSlot[s.heure] = commandes.filter(c => c.slot_pickup === s.heure);
  }

  if (slots.length === 0) {
    return (
      <div className="jj-liste">
        <div className="empty-state">
          <div className="empty-state-icon"><Icon name="liste" size={32} /></div>
          <div className="empty-state-title">Aucun créneau configuré</div>
          <div className="empty-state-sub">Les créneaux apparaîtront ici une fois configurés dans l'événement.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="jj-liste">
      {slots.map(slot => {
        const cmdsSlot = parSlot[slot.heure] || [];
        const nbRemises = cmdsSlot.filter(c => c.statut === 'remise').length;
        const ouvert = slotOuvert === slot.heure;

        return (
          <div key={slot.heure} className="slot-groupe">
            <div
              className={`slot-groupe-header ${ouvert ? 'slot-groupe-header--ouvert' : ''}`}
              onClick={() => setSlotOuvert(ouvert ? null : slot.heure)}
            >
              <span className="slot-groupe-heure">
                <Icon name="heure" size={14} />
                {slot.heure}
              </span>
              <span className="slot-groupe-stats muted">
                {cmdsSlot.length} commandes — {nbRemises} remises
              </span>
            </div>

            {ouvert && (
              <div className="slot-groupe-body">
                {cmdsSlot.length === 0 && (
                  <div className="muted" style={{ padding: '12px 16px', fontSize: 14 }}>
                    Aucune commande sur ce créneau.
                  </div>
                )}
                {cmdsSlot.map(cmd => {
                  const items = typeof cmd.items === 'string'
                    ? JSON.parse(cmd.items) : (cmd.items || []);
                  const remise = cmd.statut === 'remise';
                  return (
                    <div
                      key={cmd.id}
                      className={`cmd-row ${remise ? 'cmd-remise' : 'cmd-attente'}`}
                      onClick={() => setCmdSel(cmdSelectionnee?.id === cmd.id ? null : cmd)}
                    >
                      <div className="cmd-row-icone">
                        {remise
                          ? <Icon name="check" size={14} />
                          : <Icon name="heure" size={14} />}
                      </div>
                      <div className="cmd-row-info">
                        <div className="cmd-row-num">{cmd.numero_conf}</div>
                        <div className="cmd-row-client muted">
                          {cmd.client_prenom} {cmd.client_nom}
                        </div>
                        <div className="cmd-row-items muted">
                          {items.map((it, i) => `${it.quantite}×${it.label || it.taille}`).join(' / ')}
                        </div>
                      </div>
                      {remise && <div className="cmd-row-remise-badge">Remis</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Mini modal détail */}
      {cmdSelectionnee && (
        <div className="jj-mini-modal-backdrop" onClick={() => setCmdSel(null)}>
          <div className="jj-mini-modal" onClick={e => e.stopPropagation()}>
            <div className="jj-mini-modal-header">
              <strong>{cmdSelectionnee.numero_conf}</strong>
              <button className="modal-close" onClick={() => setCmdSel(null)}>
                <Icon name="fermer" size={16} />
              </button>
            </div>
            <div style={{ padding: '0 16px 8px' }}>
              <div>{cmdSelectionnee.client_prenom} {cmdSelectionnee.client_nom}</div>
              <div className="muted" style={{ fontSize: 13 }}>
                {cmdSelectionnee.client_email}
              </div>
              <div style={{ marginTop: 10 }}>
                {(typeof cmdSelectionnee.items === 'string'
                  ? JSON.parse(cmdSelectionnee.items)
                  : cmdSelectionnee.items || []).map((it, i) => (
                    <div key={i} style={{ fontSize: 14 }}>
                      {it.quantite}&times; {it.label || it.taille}
                    </div>
                  ))}
              </div>
            </div>
            {cmdSelectionnee.statut !== 'remise' && (
              <div style={{ padding: '8px 16px 16px' }}>
                <button
                  className="btn btn-primary btn-full"
                  onClick={() => {
                    onMarquerStatut(cmdSelectionnee.id, 'remise');
                    setCmdSel(null);
                  }}
                  disabled={chargementAction}
                >
                  Marquer comme remise
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vue production ────────────────────────────

function VueProduction({ production, slots }) {
  if (!production || production.length === 0) {
    return (
      <div className="jj-production">
        <div className="empty-state">
          <div className="empty-state-icon"><Icon name="viande" size={32} /></div>
          <div className="empty-state-title">Aucune donnée de production</div>
          <div className="empty-state-sub">Configurez la production dans les paramètres de l'événement.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="jj-production">
      <div className="jj-section-titre">Production totale requise</div>
      <div className="prod-tableau">
        {production.map((p, i) => (
          <div key={i} className="prod-ligne">
            <div className="prod-produit">{p.produit}</div>
            <div className="prod-qte">
              {p.total_cuit_kg ? `${p.total_cuit_kg.toFixed(1)} kg` : p.total_unites}
            </div>
          </div>
        ))}
      </div>
      {slots.length > 0 && (
        <>
          <div className="jj-section-titre" style={{ marginTop: 24 }}>Par créneau</div>
          {slots.map(slot => (
            <div key={slot.heure} className="prod-slot-bloc">
              <div className="prod-slot-titre muted">{slot.heure}</div>
              <div className="prod-slot-stats">
                {slot.nb_commandes || 0} commandes · {slot.nb_personnes || 0} personnes
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── Squelette de chargement ───────────────────

function JourJSkeleton() {
  return (
    <div className="jj-page">
      <div className="jj-header">
        <div className="skeleton" style={{ width: 80, height: 20, borderRadius: 'var(--radius-sm)' }} />
        <div className="skeleton" style={{ width: 100, height: 24, borderRadius: 'var(--radius-sm)' }} />
      </div>
      <div className="jj-onglets">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton" style={{ flex: 1, height: 48, borderRadius: 'var(--radius-sm)' }} />
        ))}
      </div>
      <div className="jj-body" style={{ padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div className="jj-kpi-grille">
          {[1, 2, 3].map(i => (
            <div key={i} className="jj-kpi">
              <div className="skeleton" style={{ width: 48, height: 44, borderRadius: 'var(--radius-sm)', margin: '0 auto 8px' }} />
              <div className="skeleton" style={{ width: 60, height: 14, borderRadius: 'var(--radius-sm)', margin: '0 auto' }} />
            </div>
          ))}
        </div>
        <div className="skeleton" style={{ height: 64, borderRadius: 'var(--radius-md)' }} />
        <div className="skeleton" style={{ height: 20, borderRadius: 'var(--radius-sm)' }} />
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────

export default function JourJ() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [onglet, setOnglet]         = useState('stats');
  const [evenement, setEvenement]   = useState(null);
  const [commandes, setCommandes]   = useState([]);
  const [slots, setSlots]           = useState([]);
  const [production, setProduction] = useState([]);
  const [chargement, setCh]         = useState(true);
  const [chargementAction, setChgA] = useState(false);
  const [stats, setStats]           = useState({
    nb_remises: 0, nb_pretes: 0, nb_attente: 0,
    nb_confirmees: 0, revenus: 0,
  });

  const calculerStats = useCallback((cmds) => {
    setStats({
      nb_remises:     cmds.filter(c => c.statut === 'remise').length,
      nb_pretes:      cmds.filter(c => c.statut === 'prete').length,
      nb_attente:     cmds.filter(c => c.statut === 'en_attente').length,
      nb_confirmees:  cmds.filter(c => !['annulee','en_attente'].includes(c.statut)).length,
      revenus:        cmds
        .filter(c => !['annulee','en_attente'].includes(c.statut))
        .reduce((acc, c) => acc + (c.montant_total || 0), 0),
    });
  }, []);

  const charger = useCallback(async () => {
    try {
      const [ev, cmds, slotsData, prod] = await Promise.all([
        api.get(`/evenements/${id}`),
        api.get(`/evenements/${id}/commandes`),
        api.get(`/evenements/${id}/slots`),
        api.get(`/evenements/${id}/production`).catch(() => []),
      ]);
      setEvenement(ev);
      const cmdArr = Array.isArray(cmds) ? cmds : (cmds.commandes || []);
      setCommandes(cmdArr);
      setSlots(Array.isArray(slotsData) ? slotsData : (slotsData.slots || []));
      setProduction(Array.isArray(prod) ? prod : (prod.production || []));
      calculerStats(cmdArr);
    } catch (err) {
      console.error('[JourJ]', err);
    } finally {
      setCh(false);
    }
  }, [id, calculerStats]);

  useEffect(() => { charger(); }, [charger]);

  // Rafraîchissement auto toutes les 10 secondes
  useEffect(() => {
    const t = setInterval(charger, 10000);
    return () => clearInterval(t);
  }, [charger]);

  async function marquerStatut(cmdId, statut) {
    setChgA(true);
    try {
      await api.put(`/precommandes/${cmdId}/statut`, { statut });
      await charger();
    } catch {}
    setChgA(false);
  }

  if (chargement) {
    return <JourJSkeleton />;
  }

  return (
    <div className="jj-page">
      {/* Header */}
      <div className="jj-header">
        <button className="jj-retour" onClick={() => navigate(`/evenements/${id}`)}>
          <Icon name="retour" size={14} />
          Retour
        </button>
        <div className="jj-header-titre">Jour J</div>
      </div>

      {/* Onglets */}
      <div className="jj-onglets">
        {ONGLETS.map(o => (
          <button
            key={o.key}
            className={`jj-onglet ${onglet === o.key ? 'jj-onglet-actif' : ''}`}
            onClick={() => setOnglet(o.key)}
          >
            <Icon name={o.icon} size={15} />
            {o.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="jj-body">
        {onglet === 'stats'   && <VueStats stats={stats} evenement={evenement} />}
        {onglet === 'scanner' && <VueScanner evenementId={id} />}
        {onglet === 'liste'   && (
          <VueListe
            commandes={commandes}
            slots={slots}
            onMarquerStatut={marquerStatut}
            chargementAction={chargementAction}
          />
        )}
        {onglet === 'production' && (
          <VueProduction production={production} slots={slots} />
        )}
      </div>
    </div>
  );
}
